import React, { ReactNode } from 'react';
import { Box } from '@cp/ds/src/components/box';
import { Card } from '@cp/ds/src/components/card';
import { CardProps } from '@cp/ds/src/components/card/model';
import { Stack } from '@cp/ds/src/components/stack';
import { styled } from '@cp/ds/src/theme/styled';
import { CatalogItemViewModel } from '@common/model/catalog/item/view';
import { Typography } from '@cp/ds/src/components/typography';
import { useIsMobile } from '@cp/ds/src/hooks';
import { ResumeFeatureRender } from './resumeFeatureRender';

const Root = styled(Card)({
  borderRadius: '16px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
  '&:hover': {
    backgroundColor: 'white',
  },
});

const StyledPhoto = styled(Box)({
  position: 'relative',
  width: '100%',
  zIndex: 0,
  '&:after': {
    position: 'absolute',
    content: '""',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    zIndex: 1,
    marginBottom: '-1px',
    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 71.48%, #FFFFFF 100%)',
  },
});

const StyledHeader = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '-11px',
  left: 0,
  zIndex: 3,
  paddingInline: '8px',
  [theme.breakpoints.up('md')]: {
    paddingInline: '12px',
  },
}));

const TopInfoBlock = styled(Stack)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  padding: '6px',
  [theme.breakpoints.up('md')]: {
    padding: '12px',
  },
}));

const StyledMediaButton = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '130px',
  left: '50%',
  transform: 'translate(-50%)',
  zIndex: 1,
  [theme.breakpoints.up('md')]: {
    bottom: '120px',
  },
}));

export interface ResumeNewCardProps extends CardProps {
  hidden?: ReactNode | ReactNode[];
  header?: ReactNode;
  achievements?: ReactNode | ReactNode[];
  profilePhoto?: ReactNode;
  mediaButton?: ReactNode;
  footer?: ReactNode;
  showDescription?: boolean;
  data?: CatalogItemViewModel;
  itemPrice?: ReactNode;
}

export const ResumeCardNew: React.FC<ResumeNewCardProps> = ({
  mediaButton,
  itemPrice,
  achievements,
  profilePhoto,
  data,
  header,
  ...props
}) => {
  const isMobile = useIsMobile();

  return (
    <Root {...props}>
      <StyledMediaButton>{mediaButton}</StyledMediaButton>
      <StyledPhoto>
        {profilePhoto}
        <TopInfoBlock alignItems="flex-start" direction="row" justifyContent="space-between">
          {itemPrice}
          <Stack alignItems="flex-end" gap={0.5}>
            <ResumeFeatureRender data={data} featurePath="sidebar.right" />
            {achievements}
          </Stack>
        </TopInfoBlock>
        <StyledHeader>{header}</StyledHeader>
      </StyledPhoto>
      <Typography
        component="p"
        numberOfLines={isMobile ? 5 : 4}
        sx={{
          px: { xs: '8px', md: '12px' },
          typography: { xs: 'small text', md: 'caption' },
          mt: '15px',
          mb: { xs: '8px', md: '12px' },
        }}
      >
        {data?.translation?.description}
      </Typography>
    </Root>
  );
};
